/**
 * @licence Copyright © 2019 Mercury Redstone BV, all rights reserved
 */
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useMeasure } from 'react-use';
import { GoogleLogin, GoogleLoginProps } from '@react-oauth/google';
import { sendSentryError } from '../../utils/sentry';
import { useAlert } from '../../providers';

export type GoogleSigninButtonProps = {
  buttonProps?: Omit<GoogleLoginProps, 'onSuccess'>;
  onSuccess: (code: string) => void;
};

const GoogleSigninButton = ({
  buttonProps,
  onSuccess,
  ...props
}: GoogleSigninButtonProps) => {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const { t } = useTranslation();
  const { setAlert } = useAlert();

  return (
    <Wrapper ref={ref} {...props}>
      <GoogleLogin
        width={width}
        size={'large'}
        theme={'filled_blue'}
        shape={'pill'}
        use_fedcm_for_button
        use_fedcm_for_prompt
        onSuccess={({ credential }) => {
          if (!credential) {
            setAlert({
              type: 'error',
              message: t('GOOGLE_AUTH_BUTTON__error'),
            });
            return;
          }
          onSuccess(credential);
        }}
        onError={() => {
          setAlert({
            type: 'error',
            message: t('GOOGLE_AUTH_BUTTON__error'),
          });
          sendSentryError(new Error('Google signin error'));
          buttonProps?.onError?.();
        }}
        {...buttonProps}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  max-width: 100%;
`;

export { GoogleSigninButton };
